import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip,
  Checkbox,
  ButtonGroup,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Close as CloseIcon,
  Grass as GrassIcon,
  LocalFlorist as LocalFloristIcon,
} from '@mui/icons-material';
import { DASHBOARD_CHARTS } from '../../../../utils/dashboardChartMapping';
import {
  ChartOption,
  AVAILABLE_CHARTS,
  CATEGORY_COLORS,
  CATEGORY_NAMES,
} from '../constants/chartConstants';

interface ChartSelectionModalProps {
  open: boolean;
  onClose: () => void;
  selectedCharts: string[];
  onSave: (selectedCharts: string[]) => void;
}

const ChartSelectionModal: React.FC<ChartSelectionModalProps> = ({
  open,
  onClose,
  selectedCharts,
  onSave,
}) => {
  const [localSelectedCharts, setLocalSelectedCharts] = useState<string[]>(selectedCharts);

  useEffect(() => {
    setLocalSelectedCharts(selectedCharts);
  }, [selectedCharts]);

  const handleToggleChart = (chartId: string) => {
    setLocalSelectedCharts((prev) => {
      if (prev.includes(chartId)) {
        return prev.filter((id) => id !== chartId);
      }
      return [...prev, chartId];
    });
  };

  const handleSelectAll = () => {
    setLocalSelectedCharts(AVAILABLE_CHARTS.map((chart) => chart.id));
  };

  const handleDeselectAll = () => {
    setLocalSelectedCharts([]);
  };

  const handleSelectDashboard = (dashboardType: 'soil' | 'plant') => {
    setLocalSelectedCharts(DASHBOARD_CHARTS[dashboardType]);
  };

  const handleSave = () => {
    onSave(localSelectedCharts);
  };

  const handleCancel = () => {
    setLocalSelectedCharts(selectedCharts);
    onClose();
  };

  const groupedCharts = AVAILABLE_CHARTS.reduce((acc, chart) => {
    if (!acc[chart.category]) {
      acc[chart.category] = [];
    }
    acc[chart.category].push(chart);
    return acc;
  }, {} as Record<string, ChartOption[]>);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <AnalyticsIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="600" color="text.primary">
                Seleccionar Gráficos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Personaliza qué gráficos incluir en este informe quincenal
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={handleCancel}
            color="inherit"
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Selection Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1" fontWeight="600">
              Resumen de selección
            </Typography>
            <Chip
              label={`${localSelectedCharts.length} de ${AVAILABLE_CHARTS.length} seleccionados`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>

          <Box display="flex" gap={1} mb={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAll}
              sx={{ textTransform: 'none' }}
            >
              Seleccionar todos
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDeselectAll}
              color="secondary"
              sx={{ textTransform: 'none' }}
            >
              Deseleccionar todos
            </Button>
          </Box>

          {/* Dashboard Presets */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" mb={1}>
              Visualizaciones preconfiguradas
            </Typography>
            <ButtonGroup variant="outlined" size="small">
              <Button
                startIcon={<GrassIcon />}
                onClick={() => handleSelectDashboard('soil')}
                sx={{ textTransform: 'none' }}
              >
                Visualización Suelo
              </Button>
              <Button
                startIcon={<LocalFloristIcon />}
                onClick={() => handleSelectDashboard('plant')}
                sx={{ textTransform: 'none' }}
              >
                Visualización Planta
              </Button>
            </ButtonGroup>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {Object.entries(groupedCharts).map(([category, charts]) => (
            <Grid item xs={12} md={6} key={category}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  height: '100%',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS],
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    {CATEGORY_NAMES[category as keyof typeof CATEGORY_NAMES]}
                  </Typography>
                  <Box sx={{ ml: 'auto' }}>
                    <Chip
                      label={`${charts.filter((chart) => localSelectedCharts.includes(chart.id)).length}/${charts.length}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                <Box>
                  {charts.map((chart) => (
                    <Box
                      key={chart.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'grey.50',
                          borderColor: 'grey.300',
                          transform: 'translateY(-1px)',
                        },
                        '&:last-child': {
                          mb: 0,
                        },
                      }}
                      onClick={() => handleToggleChart(chart.id)}
                    >
                      <Checkbox
                        checked={localSelectedCharts.includes(chart.id)}
                        onChange={() => handleToggleChart(chart.id)}
                        color="primary"
                        size="small"
                        sx={{
                          mt: -0.5,
                          mr: 2,
                          p: 0.5,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          color="text.primary"
                          sx={{
                            lineHeight: 1.4,
                            mb: chart.description ? 0.5 : 0,
                          }}
                        >
                          {chart.name}
                        </Typography>
                        {chart.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.3,
                              display: 'block',
                            }}
                          >
                            {chart.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ textTransform: 'none', px: 3 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={localSelectedCharts.length === 0}
          sx={{ textTransform: 'none', px: 4 }}
        >
          Aplicar cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartSelectionModal;
